class SiteElementChat
    extends QueryElement<HTMLElement> {

    private _card: SiteCard;
    private _messages: SiteElementChatMessages;
    private _input: SiteElementChatInput;
    private _disposeEvent: (e: CustomEvent<any>) => any;
    private _chatId: string;

    constructor(
        card: SiteCard,
        model: ISiteModel, 
        elem: HTMLElement) {

        super(elem);

        this._card = card;
        this._messages = new SiteElementChatMessages();
        this._input = new SiteElementChatInput();

        var path = this.getAttr("data-path");
        if (path == null)
            throw "Unable to get path!";

        this._chatId = model.get(SiteModel.split(path));

        this._disposeEvent = this._input.on("qts.input", (e: CustomEvent) => {
            this.submit(e.detail);
        })

        this._messages.addMessage(new SiteElementChatMessageNotice("Welkom, ik ben EasyBot, je assistent. Stel je vraag of zeg: 'Wat kun je?'"));

        this.append(this._messages);
        this.append(this._input);
    }
    async submit(message: string) {
        this._messages.addMessage(new SiteElementChatMessageUser(message));
        var responseTask = this.requestResponse(message);
        
        await delay(900);
        var waiting = new SiteElementChatMessageAssistant(this, { 
            text: "<div class='waiting'><span></span><span></span><span></span></div>" 
        });
        this._messages.addMessage(waiting);
        
        var response = await responseTask;
        waiting.remove();

        this._messages.addMessage(new SiteElementChatMessageAssistant(this, response));
    }

    getCard() {
        return this._card;
    }

    async requestResponse(message: string) {
        let queryRequest = await new QueryRequest(
            "AI/AI/Submit",
            {
                contentType: 'json',
                data: JSON.stringify(
                    {
                        id: this._chatId,
                        message: message
                    }
                ),
                method: 'post'
            }
        ).request();

        return await queryRequest.json() as ISiteElementChatAnswerAssistant;
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
        this._input.off("input", this._disposeEvent);
    }
}

class SiteElementChatMessages
    extends QueryElement<HTMLElement> {
    
    constructor() {
        super(document.createElement("div"));
        this.addClass("messages")
    }
    addMessage(element: QueryElement<HTMLElement>) {
        QueryElement.QueryElement("div")
            .addClass("message-container")
            .append(element)
            .appendTo(this);

        element.get()?.scrollIntoView();
    }
}

class SiteElementChatMessageNotice
    extends QueryElement<HTMLElement> {

    constructor(text: string) {
        super(document.createElement("div"));

        this.addClass("notice");

        this.setHtml(text.replace(/\n/g, '<br>'));
    }
}

class SiteElementChatMessageUser
    extends QueryElement<HTMLElement> {

    constructor(text: string) {
        super(document.createElement("div"));

        this.addClass("message");
        this.addClass("user");

        this.setHtml(text.replace(/\n/g, '<br>'));
    }
}

type SiteChatElementChatContentTypes = "Paragraph" | "Button" | "ButtonBar" | "List" | "Definition";
type SiteChatElementChatContentOverflows = "Top" | "Bottom";
type SiteElementChatContentButtonActions = "CardAction" | "SpecialAction";
type SiteElementChatContentButtonSpecialActions = "UserMessage";

interface ISiteElementChatContent {
    type: SiteChatElementChatContentTypes;
}
interface ISiteElementChatContentParagraph
    extends ISiteElementChatContent {
    
    text: string;
}


interface ISiteElementChatContentButtonAction {
    type: SiteElementChatContentButtonActions;
}
interface ISiteElementChatContentButtonCardAction
    extends ISiteElementChatContentButtonAction {
    action: IActionType;
}


interface ISiteElementChatContentButtonSpecialAction
    extends ISiteElementChatContentButtonAction {
    specialAction: SiteElementChatContentButtonSpecialActions;
}

interface ISiteElementChatContentButtonSpecialActionUserMessage
    extends ISiteElementChatContentButtonSpecialAction {
    
    text: string;
}

interface ISiteElementChatContentButton
    extends ISiteElementChatContent {
    
    iconLeft?: Icons;
    iconRight?: Icons;
    content: ISiteElementChatContent;
    action: ISiteElementChatContentButtonAction;
}
interface ISiteElementChatContentButtonBar
    extends ISiteElementChatContent {
    
    buttons: ISiteElementChatContent[];
}
interface ISiteElementChatContentList
    extends ISiteElementChatContent {
    
    list: ISiteElementChatContent[];
}
interface ISiteElementChatContentDefinition
    extends ISiteElementChatContent {
    
    name: string;
    value: string;
}
interface ISiteElementChatAnswerAssistant {
    text: string;
    content?: ISiteElementChatContent[];
    overflows?: SiteChatElementChatContentOverflows[];

}
class SiteElementChatMessageAssistant
    extends QueryElement<HTMLElement> {

    constructor(chat: SiteElementChat, answer: ISiteElementChatAnswerAssistant) {
        super(document.createElement("div"));

        this.addClass("message");
        this.addClass("assistant");

        if (answer.content != null)
        {
            if (answer.overflows?.includes("Bottom"))
                this.addClass("overflow-bottom");
            if (answer.overflows?.includes("Top"))
                this.addClass("overflow-top");

            for(var content of answer.content) {
                SiteElementChatMessageAssistant
                    .getElement(chat, content)
                    ?.appendTo(this);
            }
        }
        else
        {
            this.setHtml(answer.text.replace(/\n/g, '<br>'));
        }
    }

    static getElement(chat: SiteElementChat, input: ISiteElementChatContent) {
        switch(input.type)
        {
            case "Paragraph":
                return new SiteElementChatContentParagraph(input as ISiteElementChatContentParagraph);
            case "ButtonBar":
                return new SiteElementChatContentButtonBar(chat, input as ISiteElementChatContentButtonBar);
            case "Button":
                return new SiteElementChatContentButton(chat, input as ISiteElementChatContentButton);
            case "Definition":
                return new SiteElementChatContentDefinition(input as ISiteElementChatContentDefinition);
            case "List":
                return new SiteElementChatContentList(chat, input as ISiteElementChatContentList);
        }
    }
}
class SiteElementChatContentParagraph
    extends QueryElement<HTMLElement> {

    constructor(paragraph: ISiteElementChatContentParagraph) {
        super(document.createElement("p"));

        this.setHtml(paragraph.text);
    }
}
class SiteElementChatContentButton
    extends QueryElement<HTMLElement> {

    constructor(chat: SiteElementChat, button: ISiteElementChatContentButton) {
        super(document.createElement("div"));

        this.addClass("content-button");

        if (button.iconLeft != null)
            QueryElement.QueryElement("i")
                .addClass("icon-left")
                .setText(button.iconLeft)
                .appendTo(this);

        SiteElementChatMessageAssistant
            .getElement(chat, button.content)
            ?.appendTo(this);
        

        if (button.iconRight != null)
            QueryElement.QueryElement("i")
                .addClass("icon-right")
                .setText(button.iconRight)
                .appendTo(this);
        
        this.on("click", () => {
            switch(button.action.type) {
                case "CardAction":
                    var cardAction = button.action as ISiteElementChatContentButtonCardAction;
                    SiteAction.create(chat.getCard(), cardAction.action)?.execute();
                    break;
                case "SpecialAction":
                    var specialAction = button.action as ISiteElementChatContentButtonSpecialAction;
                    switch(specialAction.specialAction)
                    {
                        case "UserMessage":
                            var userMessage = specialAction as ISiteElementChatContentButtonSpecialActionUserMessage;
                            chat.submit(userMessage.text);
                            break;
                    }

                    break;
            }
        })
    }
}
class SiteElementChatContentDefinition
    extends QueryElement<HTMLElement> {

    constructor(definition: ISiteElementChatContentDefinition) {
        super(document.createElement("div"));

        this.addClass("content-definition");
        
        QueryElement.QueryElement("div")
            .setHtml(definition.name)
            .appendTo(this);
        QueryElement.QueryElement("div")
            .setHtml(definition.value)
            .appendTo(this);
    }
}
class SiteElementChatContentButtonBar
    extends QueryElement<HTMLElement> {

    constructor(chat: SiteElementChat, bar: ISiteElementChatContentButtonBar) {
        super(document.createElement("div"));
        
        this.addClass("content-buttonbar");

        for(var content of bar.buttons) {
            SiteElementChatMessageAssistant
                .getElement(chat, content)
                ?.appendTo(this);
        }
        
    }
}
class SiteElementChatContentList
    extends QueryElement<HTMLElement> {

    constructor(chat: SiteElementChat, list: ISiteElementChatContentList) {
        super(document.createElement("div"));
        
        this.addClass("content-list");

        for(var content of list.list) {
            SiteElementChatMessageAssistant
                .getElement(chat, content)
                ?.appendTo(this);
        }
        
    }
}

class SiteElementChatInput
    extends QueryElement<HTMLElement> {

    private _input: QueryElement<HTMLElement>;
    private _submit: QueryElement<HTMLElement>;
    private _disposeEventSubmit: (e: Event) => any;
    private _disposeEventInput: (e: KeyboardEvent) => any;
    private readonly _maxHeight: number = 80;
    private _disposeEventKeyDown: (e: KeyboardEvent) => any;

    constructor() {
        super(document.createElement("div"));

        this.addClass("input");

        this._input = QueryElement.QueryElement("textarea")
            .setAttr("placeholder", "Stel hier je vragen...")
            .appendTo(this);
        this._submit = QueryElement.QueryElement("div")
            .addClass("send")
            .append(QueryElement.QueryElement("i").setText("send"))
            .appendTo(this);
        
        this._disposeEventSubmit = this._submit.on("click", (e) => {
            this.submit();
        });
        this._disposeEventInput = this._input.on("input",(e: KeyboardEvent) => {
            let textarea = this._input.get();
            if (textarea != null) {
                textarea.style.height = "auto";
                textarea.style.height = Math.min(textarea.scrollHeight, this._maxHeight) + "px";
            }
        });
        this._disposeEventKeyDown = this._input.on("keydown", (e: KeyboardEvent) => {
            switch(e.keyCode) {
                case 13:
                    if (!e.shiftKey && !e.ctrlKey) {
                        this.submit();
                        e.stop();
                    }
                    break;
            }


        });
    }

    submit() {
        this.trigger("qts.input", false, this._input.getVal());
        this._input.setVal("");
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
        this._submit.off("click", this._disposeEventSubmit);
        this._input.off("input", this._disposeEventInput);
        this._input.off("keydown", this._disposeEventKeyDown);
    }
}